/**
 * Use-Case-Registry (P2, UI-Overhaul 2026-07-07).
 *
 * Diese Liste ist die Skalierungs-Mechanik der Atlas-Startseite: jeder neue
 * Use-Case ist genau EIN Eintrag hier — Home-Grid, Zonen-Gruppierung und
 * „Bald verfügbar"-Zustand leiten sich automatisch daraus ab.
 *
 * Kein API-Call, keine Logik — reine Deklaration. Icons sind lucide-react
 * Komponenten (kein JSX hier, damit die Datei .ts bleibt).
 *
 * DE/EN: Die Texte stehen als GETTER auf den Einträgen. Diese Liste ist eine
 * Modul-Konstante — würde der Text beim Laden EINMAL aus dem Katalog gelesen,
 * bliebe die Startseite nach dem Umschalten in der alten Sprache stehen. Der
 * Getter liest bei jedem Render; die Konsumenten (Overview.tsx) bleiben
 * unverändert, weil `title`/`description`/`chip` weiterhin Strings sind.
 */
import type { LucideIcon } from "lucide-react";
import { katalog } from "./i18n/lang";
import {
  MessageSquare,
  FileUp,
  NotebookPen,
  ListTodo,
  CalendarClock,
  Mail,
  PenLine,
  Video,
  Beaker,
  Table2,
} from "lucide-react";

export type UseCaseZone = "alltag" | "studio";

export interface UseCase {
  id: string;
  title: string;
  /** Ein Satz konkreter Nutzen, deutsch. Wird auf dem Home-Chip zum title-Tooltip. */
  description: string;
  icon: LucideIcon;
  /** Ziel-Route ODER eine spezielle Aktion (öffnet z. B. Quick-Capture). */
  to?: string;
  action?: "quick-capture";
  zone: UseCaseZone;
  /** true = „Bald verfügbar", Karte ausgegraut + nicht klickbar. */
  soon?: boolean;
  /** Kurz-Label (2-3 Worte) für die Home-Chip-Zeile. Ohne Wert = kein Home-Chip. */
  chip?: string;
}

/**
 * Reihenfolge der Home-Chips (Icon + Kurz-Label, kein Erklärtext).
 * Bezieht sich auf UseCase.id; die Kurz-Labels stehen als `chip` am Eintrag.
 *
 * **`blog` und `video` stehen hier bewusst NICHT mehr** (2026-07-31). Sie zeigen
 * auf Studio-Ziele, die nur Wolfgang sieht (studioAccess.ts), und die Antwort
 * darauf kommt erst vom Server. Ein Chip-Band, dessen Inhalt sich ~100 ms nach
 * dem Laden ändert, verschiebt Klickziele — nachgemessen mit dem Chip-Band als
 * `flex flex-wrap justify-center` (Overview.tsx):
 *
 *   · zentriert → beim Dazukommen rutschen ALLE vorhandenen Chips 63 px nach
 *     links, auch wenn die neuen ans Ende gehängt werden;
 *   · sechs Chips brauchen eine zweite Zeile → Bandhöhe 34 → 76 px, alles
 *     darunter (Aufgaben-Zeile, „Zuletzt"-Liste) rutscht 42 px nach unten.
 *
 * Beide Ziele bleiben über die linke Leiste erreichbar (ein Klick, dort steht
 * die Gruppe am Ende und verschiebt nichts). Wer die Chips zurückholen will,
 * braucht zweierlei: eine Antwort auf die Geometrie (feste Bandhöhe oder
 * linksbündige, nicht umbrechende Zeile), und er muss die Invariante in
 * Overview.tsx aufheben — die filtert Studio-Ziele hier unabhängig von dieser
 * Liste heraus, damit die Regel nicht ein zweites Mal nur halb greift.
 */
export const HOME_CHIP_IDS = ["upload", "tasks", "note", "appointment"] as const;

export const USE_CASES: UseCase[] = [
  {
    id: "ask",
    get title() {
      return katalog().shared.useCases.ask.titel;
    },
    get description() {
      return katalog().shared.useCases.ask.beschreibung;
    },
    icon: MessageSquare,
    to: "/chat",
    zone: "alltag",
  },
  {
    id: "upload",
    get title() {
      return katalog().shared.useCases.upload.titel;
    },
    get description() {
      return katalog().shared.useCases.upload.beschreibung;
    },
    icon: FileUp,
    // Kein `to: /docs` mehr (war doppelt zur 📎). Der Chip bleibt ein Link auf
    // die Startseite mit `?attach=doc`; der Home-Composer liest den Param und
    // öffnet den Datei-Dialog (kein Overview-Eingriff nötig).
    to: "/?attach=doc",
    zone: "alltag",
    get chip() {
      return katalog().shared.useCases.upload.chip;
    },
  },
  {
    id: "note",
    get title() {
      return katalog().shared.useCases.note.titel;
    },
    get description() {
      return katalog().shared.useCases.note.beschreibung;
    },
    icon: NotebookPen,
    action: "quick-capture",
    zone: "alltag",
    get chip() {
      return katalog().shared.useCases.note.chip;
    },
  },
  {
    id: "tasks",
    get title() {
      return katalog().shared.useCases.tasks.titel;
    },
    get description() {
      return katalog().shared.useCases.tasks.beschreibung;
    },
    icon: ListTodo,
    to: "/tasks",
    zone: "alltag",
    get chip() {
      return katalog().shared.useCases.tasks.chip;
    },
  },
  {
    id: "appointment",
    get title() {
      return katalog().shared.useCases.appointment.titel;
    },
    get description() {
      return katalog().shared.useCases.appointment.beschreibung;
    },
    icon: CalendarClock,
    to: "/calendar",
    zone: "alltag",
    get chip() {
      return katalog().shared.useCases.appointment.chip;
    },
  },
  {
    id: "email",
    get title() {
      return katalog().shared.useCases.email.titel;
    },
    get description() {
      return katalog().shared.useCases.email.beschreibung;
    },
    icon: Mail,
    to: "/mail",
    zone: "alltag",
  },
  {
    id: "blog",
    get title() {
      return katalog().shared.useCases.blog.titel;
    },
    get description() {
      return katalog().shared.useCases.blog.beschreibung;
    },
    icon: PenLine,
    to: "/blog",
    zone: "studio",
    get chip() {
      return katalog().shared.useCases.blog.chip;
    },
  },
  {
    id: "video",
    get title() {
      return katalog().shared.useCases.video.titel;
    },
    get description() {
      return katalog().shared.useCases.video.beschreibung;
    },
    icon: Video,
    to: "/heygen",
    zone: "studio",
    get chip() {
      return katalog().shared.useCases.video.chip;
    },
  },
  {
    id: "claims",
    get title() {
      return katalog().shared.useCases.claims.titel;
    },
    get description() {
      return katalog().shared.useCases.claims.beschreibung;
    },
    icon: Beaker,
    to: "/claims",
    zone: "studio",
  },
  {
    id: "datasets",
    get title() {
      return katalog().shared.useCases.datasets.titel;
    },
    get description() {
      return katalog().shared.useCases.datasets.beschreibung;
    },
    icon: Table2,
    to: "/ds",
    zone: "studio",
  },
];
